import { addDays, isIsoDate } from "./dates";
import { MAX_DOCUMENT_CENTS } from "./quotes";
import { MAX_STOCK_BALANCE_Q4 } from "./stock";

// ===== Dashboards e metas (T1.13, RF-BI-01..09) =====

/** Período do Mensal e o de comparação: até 366 dias cada (custo da leitura limitado ao período, nunca ao histórico). */
export const MAX_DASHBOARD_PERIOD_DAYS = 366;
/** Situações no filtro (`statuses`). */
export const MAX_DASHBOARD_STATUSES = 30;
/** Situações de sistema que contam como venda por padrão (PRD RF-BI-01). */
export const DASHBOARD_DEFAULT_STATUS_CODES = ["em_andamento", "verificado", "atendido"] as const;
/** Série de vendedor/canal (RF-BI-09). */
export const SALES_SERIES_MONTHS = 24;
/** Linhas no ranking de 24 meses; o resto vira "Outros". */
export const SALES_RANKING_LIMIT = 20;
/** Hora "sem horário": pedido com data diferente do dia (local) em que foi criado. */
export const NO_HOUR = -1;
/** Metas não excluídas por empresa. */
export const MAX_GOALS_PER_COMPANY = 500;
export const MAX_GOAL_PERIOD_DAYS = 366;
/** Metas em andamento no card do Mensal. */
export const MAX_DASHBOARD_GOALS = 10;
/** Imposto e taxa de venda (pct4): até 100%. */
export const MAX_DASHBOARD_PCT4 = 1_000_000;

export const GOAL_METRICS = ["revenue", "orders", "items", "avg_ticket", "new_customers"] as const;
export type GoalMetric = (typeof GOAL_METRICS)[number];
export const GOAL_SCOPES = ["company", "seller"] as const;
export type GoalScope = (typeof GOAL_SCOPES)[number];
export type GoalState = "current" | "future" | "past";

/** Alvo por métrica: centavos (receita, ticket), q4 (itens) ou contagem (pedidos, novos clientes). */
export const MAX_GOAL_TARGET: Readonly<Record<GoalMetric, number>> = {
  revenue: 1_000_000_000_000_000,
  avg_ticket: MAX_DOCUMENT_CENTS,
  items: MAX_STOCK_BALANCE_Q4,
  orders: 1_000_000_000,
  new_customers: 1_000_000_000,
};

export const BRAZIL_UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const;
export type Uf = (typeof BRAZIL_UFS)[number];
export const isUf = (s: string): s is Uf => (BRAZIL_UFS as readonly string[]).includes(s);

// ---------- datas ----------

const DAY_MS = 86_400_000;
const ms = (iso: string) => Date.parse(`${iso}T00:00:00Z`);

/** Dias do período, contando as duas pontas (datas já válidas). */
export const periodDays = (from: string, to: string): number => Math.round((ms(to) - ms(from)) / DAY_MS) + 1;

/** Problema do período (datas já válidas) ou null. */
export function dashboardPeriodProblem(
  from: string,
  to: string,
  max = MAX_DASHBOARD_PERIOD_DAYS,
): string | null {
  if (from > to) return "A data de início deve ser anterior ou igual à data final";
  if (periodDays(from, to) > max) return `O período pode ter no máximo ${max} dias`;
  return null;
}

/** Último dia do mês `AAAA-MM`. */
export function lastDayOfMonth(month: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const d = new Date(0);
  d.setUTCFullYear(y, m, 0);
  return `${month}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/** Mês anterior de `AAAA-MM`. */
export function previousMonth(month: string): string {
  return addDays(`${month}-01`, -1).slice(0, 7);
}

/** Meses `AAAA-MM` em ordem, terminando em `end` (inclusive). */
export function monthsBack(end: string, n: number): string[] {
  const out = [end];
  while (out.length < n) out.unshift(previousMonth(out[0]!));
  return out;
}

/** Período padrão do Mensal: do dia 1º do mês até hoje. */
export const defaultDashboardPeriod = (today: string) => ({ from: `${today.slice(0, 7)}-01`, to: today });

/**
 * Comparação padrão: período que começa no dia 1º e termina no mesmo mês ⇒ mesmo intervalo de dias do mês anterior
 * (mês inteiro ⇒ mês anterior inteiro; o fim é limitado ao último dia do mês anterior). Qualquer outro ⇒ o período
 * imediatamente anterior, de mesmo tamanho.
 */
export function defaultComparePeriod(from: string, to: string): { from: string; to: string } {
  const month = from.slice(0, 7);
  if (from.endsWith("-01") && to.slice(0, 7) === month) {
    const prev = previousMonth(month);
    const prevLast = lastDayOfMonth(prev);
    const end = to === lastDayOfMonth(month) ? prevLast : `${prev}-${to.slice(8)}`;
    return { from: `${prev}-01`, to: end > prevLast ? prevLast : end };
  }
  const end = addDays(from, -1);
  return { from: addDays(end, 1 - periodDays(from, to)), to: end };
}

/** Comparação padrão do Diário: mesmo dia da semana anterior. */
export const defaultCompareDay = (date: string): string => addDays(date, -7);

/** Dias do período em ordem. */
export function daysOf(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

/**
 * Dia e hora locais (America/Sao_Paulo, UTC−3 fixo: sem horário de verão desde 2019) de um instante ISO. A mesma conta
 * da SQL dos fatos (`date(created_at, '-3 hours')`).
 */
export function saoPauloDayHour(instant: string): { day: string; hour: number } {
  const t = new Date(Date.parse(instant) - 3 * 3_600_000).toISOString();
  return { day: t.slice(0, 10), hour: Number(t.slice(11, 13)) };
}

/** Hora do pedido no Diário: a da criação quando o pedido é do dia (local) em que foi criado; senão NO_HOUR. */
export function orderHour(orderDate: string, createdAt: string): number {
  const l = saoPauloDayHour(createdAt);
  return l.day === orderDate ? l.hour : NO_HOUR;
}

// ---------- contas ----------

/** Soma de `total()` (float) do SQLite: exata enquanto inteira e segura; fora disso, null ("—" na tela). */
export const safeTotal = (v: number | null | undefined): number | null =>
  v != null && Number.isSafeInteger(v) ? v : null;

const safeBig = (v: bigint): number | null => {
  const n = Number(v);
  return Number.isSafeInteger(n) ? n : null;
};

/** Divisão com arredondamento comercial (meio para longe do zero); `den` > 0. */
function roundDiv(num: bigint, den: bigint): bigint {
  const neg = num < 0n;
  const a = neg ? -num : num;
  const q = (2n * a + den) / (2n * den);
  return neg ? -q : q;
}

/** Raiz quadrada inteira (piso) de um BigInt não negativo. */
export function isqrt(n: bigint): bigint {
  if (n < 2n) return n;
  let x = n;
  let y = (x + 1n) / 2n;
  while (y < x) {
    x = y;
    y = (x + n / x) / 2n;
  }
  return x;
}

/** Soma de valores possivelmente desconhecidos (null propaga; resultado não seguro = null). */
export function sumOrNull(values: readonly (number | null)[]): number | null {
  let s = 0n;
  for (const v of values) {
    if (v === null) return null;
    s += BigInt(v);
  }
  return safeBig(s);
}

/** num/den em pct4 (100% = 1.000.000); den ≤ 0 ou desconhecido = null. */
export function ratioPct4(num: number | null, den: number | null): number | null {
  if (num === null || den === null || den <= 0) return null;
  return safeBig(roundDiv(BigInt(num) * 1_000_000n, BigInt(den)));
}

/** Variação % do atual sobre o anterior (pct4), sobre o módulo do anterior; anterior zero ou desconhecido = null. */
export function variationPct4(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  const p = BigInt(previous);
  return safeBig(roundDiv((BigInt(current) - p) * 1_000_000n, p < 0n ? -p : p));
}

/** Ticket médio (centavos). */
export function avgTicket(totalCents: number | null, orders: number | null): number | null {
  if (totalCents === null || orders === null || orders <= 0) return null;
  return safeBig(roundDiv(BigInt(totalCents), BigInt(orders)));
}

/** valor × pct4 (centavos, BigInt). */
const pctOf = (v: number, pct4: number) => roundDiv(BigInt(v) * BigInt(pct4), 1_000_000n);

export type Kpi = { current: number | null; previous: number | null; variationPct4: number | null };
export const kpi = (current: number | null, previous: number | null): Kpi => ({
  current,
  previous,
  variationPct4: variationPct4(current, previous),
});

export type DreInput = {
  totalCents: number | null;
  freightCents: number | null;
  /** Custo conhecido; null = sem permissão de custo ou soma desconhecida. */
  costCents: number | null;
  taxPct4: number;
  feePct4: number;
};
export type DreValues = {
  /** Faturamento sem frete. */
  netRevenueCents: number | null;
  costCents: number | null;
  grossMarginPct4: number | null;
  taxesCents: number | null;
  feesCents: number | null;
  profitCents: number | null;
  profitPct4: number | null;
};

/**
 * DRE resumida (RF-BI-03): faturamento sem frete; impostos e taxa de venda = % sobre ele; margem bruta = (fat − custo) /
 * fat; lucro = fat − custo − impostos − taxas. Sem custo, margem e lucro ficam null.
 */
export function dreValues(i: DreInput): DreValues {
  const net =
    i.totalCents === null || i.freightCents === null ? null : sumOrNull([i.totalCents, -i.freightCents]);
  const taxes = net === null ? null : safeBig(pctOf(net, i.taxPct4));
  const fees = net === null ? null : safeBig(pctOf(net, i.feePct4));
  const cost = i.costCents;
  const gross = net === null || cost === null ? null : sumOrNull([net, -cost]);
  const profit = gross === null || taxes === null || fees === null ? null : sumOrNull([gross, -taxes, -fees]);
  return {
    netRevenueCents: net,
    costCents: cost,
    grossMarginPct4: ratioPct4(gross, net),
    taxesCents: taxes,
    feesCents: fees,
    profitCents: profit,
    profitPct4: ratioPct4(profit, net),
  };
}

// ---------- previsão do mês (RF-BI-06) ----------

export type MonthForecast = {
  actualCents: number;
  projectedCents: number;
  lowCents: number;
  highCents: number;
  monthEnd: string;
};

/**
 * Projeção do fechamento do mês: realizado + média diária dos dias decorridos (hoje incluso) × dias restantes, com
 * intervalo de 80% (±1,28 × desvio-padrão amostral diário × √dias restantes) e piso no realizado. Null com menos de 3
 * dias decorridos, no último dia do mês, com valor desconhecido ou resultado fora do seguro. Tudo em BigInt.
 */
export function forecastMonthClose(today: string, daily: readonly (number | null)[]): MonthForecast | null {
  const monthEnd = lastDayOfMonth(today.slice(0, 7));
  const n = Number(today.slice(8));
  const remaining = Number(monthEnd.slice(8)) - n;
  if (n < 3 || remaining <= 0 || daily.length < n) return null;
  let s = 0n;
  let q = 0n;
  for (const v of daily.slice(0, n)) {
    if (v === null) return null;
    s += BigInt(v);
    q += BigInt(v) * BigInt(v);
  }
  const N = BigInt(n);
  const r = BigInt(remaining);
  const projected = s + roundDiv(s * r, N);
  // (1,28)² = 1,6384 → 16384/10000; variância amostral = (NΣx² − S²) / (N(N−1)).
  const spread = isqrt(roundDiv(16_384n * (N * q - s * s) * r, 10_000n * N * (N - 1n)));
  const low = projected - spread < s ? s : projected - spread;
  const out = [s, projected, low, projected + spread].map(safeBig);
  if (out.some((v) => v === null)) return null;
  const [actual, proj, lo, hi] = out as number[];
  return { actualCents: actual!, projectedCents: proj!, lowCents: lo!, highCents: hi!, monthEnd };
}

// ---------- metas (RF-BI-05) ----------

export type GoalSums = {
  totalCents: number | null;
  orders: number | null;
  itemsQ4: number | null;
  newCustomers: number | null;
};

/** Realizado da meta pela métrica. */
export function goalActual(metric: GoalMetric, s: GoalSums): number | null {
  switch (metric) {
    case "revenue":
      return s.totalCents;
    case "orders":
      return s.orders;
    case "items":
      return s.itemsQ4;
    case "avg_ticket":
      return avgTicket(s.totalCents, s.orders);
    case "new_customers":
      return s.newCustomers;
  }
}

export function goalState(start: string, end: string, today: string): GoalState {
  if (today < start) return "future";
  return today > end ? "past" : "current";
}

/** % do período já decorrido (hoje incluso), em pct4: o ritmo esperado da meta. */
export function goalElapsedPct4(start: string, end: string, today: string): number {
  const st = goalState(start, end, today);
  if (st !== "current") return st === "future" ? 0 : 1_000_000;
  return ratioPct4(periodDays(start, today), periodDays(start, end))!;
}

export type GoalValue = {
  metric: GoalMetric;
  scope: GoalScope;
  sellerId: string | null;
  target: number;
  periodStart: string;
  periodEnd: string;
};

/** Regras da meta (formatos já válidos): período e alvo pela métrica. `{ field, message }` ou null. */
export function goalProblem(g: GoalValue): { field: string; message: string } | null {
  if (!isIsoDate(g.periodStart) || !isIsoDate(g.periodEnd))
    return { field: "periodEnd", message: "Informe o período" };
  const p = dashboardPeriodProblem(g.periodStart, g.periodEnd, MAX_GOAL_PERIOD_DAYS);
  if (p) return { field: "periodEnd", message: p };
  if (!Number.isSafeInteger(g.target) || g.target < 1 || g.target > MAX_GOAL_TARGET[g.metric])
    return { field: "target", message: "Alvo fora do limite" };
  if ((g.scope === "seller") !== (g.sellerId !== null))
    return { field: "sellerId", message: "Escolha o vendedor da meta" };
  return null;
}

// ---------- configuração (RF-BI-14, parte) ----------

export type DashboardSettingsValue = { saleStatusIds: string[]; taxPct4: number; salesFeePct4: number };

/** Situações existentes e do módulo de pedidos; percentuais de 0 a 100%. `{ field, message }` ou null. */
export function dashboardSettingsProblem(
  s: DashboardSettingsValue,
  statusIds: ReadonlySet<string>,
): { field: string; message: string } | null {
  if (s.saleStatusIds.length === 0 || s.saleStatusIds.length > MAX_DASHBOARD_STATUSES)
    return { field: "saleStatusIds", message: "Escolha as situações que contam como venda" };
  if (new Set(s.saleStatusIds).size !== s.saleStatusIds.length)
    return { field: "saleStatusIds", message: "Situação repetida" };
  if (s.saleStatusIds.some((id) => !statusIds.has(id)))
    return { field: "saleStatusIds", message: "Situação inválida" };
  for (const k of ["taxPct4", "salesFeePct4"] as const)
    if (!Number.isSafeInteger(s[k]) || s[k] < 0 || s[k] > MAX_DASHBOARD_PCT4)
      return { field: k, message: "Use um percentual entre 0% e 100%" };
  return null;
}

// ---------- ranking ----------

/**
 * Ordena chaves pelo total (desconhecido = acima de 2^53, vem primeiro; empate pela chave) e separa as `limit`
 * primeiras do resto ("Outros").
 */
export function rankKeys<K extends string>(
  totals: ReadonlyMap<K, number | null>,
  limit: number,
): { top: K[]; others: K[] } {
  const keys = [...totals.keys()].sort((a, b) => {
    const x = totals.get(a) ?? Infinity;
    const y = totals.get(b) ?? Infinity;
    return x === y ? a.localeCompare(b) : y - x;
  });
  return { top: keys.slice(0, limit), others: keys.slice(limit) };
}
